import React, { type ReactNode } from "react"
import { Navigate, useLocation, useParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { automationRightsOf } from "./hub-rights.ts"

const useOpensCommunication = () =>
  useAppSelector(
    (state) => !!state.auth?.permissions?.pages?.includes("communication")
  )

/** Whether the Communication → Templates tab will open for this person, by the same rules as its route. */
export const useOpensCommunicationTemplates = () => {
  const rights = useCommunicationRights()
  const opens = useOpensCommunication()
  return opens && rights.templatesUse
}

/** Whether the Communication → Automations tab will open for this person, by the same rules as its route. */
export const useOpensCommunicationAutomations = () => {
  const rights = useCommunicationRights()
  const opens = useOpensCommunication()
  const codes = useAppSelector(
    (state) => state.auth?.permissions?.codes?.settings?.automations
  ) as
    | {
        settings_automation_view?: boolean
        settings_automation_manage?: boolean
      }
    | undefined
  const flag = useAppSelector(
    (state) => state.auth?.userInfo?.feature_lead_automation
  )
  return opens && automationRightsOf(rights, codes, flag).automationsView
}

/**
 * Templates live in Communication now. Someone who can only reach the old
 * Settings page (no Communication access) keeps it rather than a dead end.
 */
export const EmailTemplatesMovedRedirect = ({
  fallback
}: {
  fallback: ReactNode
}) =>
  useOpensCommunicationTemplates() ? (
    <Navigate to={RoutesPaths.communication.templates} replace />
  ) : (
    <>{fallback}</>
  )

/** Settings → Automation → Communication Automation and its builder routes open in Communication → Automations. */
export const AutomationsMovedRedirect = ({
  fallback,
  mode
}: {
  fallback: ReactNode
  mode: "list" | "new" | "edit"
}) => {
  const opens = useOpensCommunicationAutomations()
  const { id } = useParams()
  const { search } = useLocation()
  if (!opens) return <>{fallback}</>
  const params = new URLSearchParams(search)
  if (mode === "new") params.set("new", params.get("category") ?? "LEAD")
  if (mode === "edit" && id) params.set("edit", id)
  params.delete("category")
  const query = params.toString()
  return (
    <Navigate
      to={`${RoutesPaths.communication.automations}${query ? `?${query}` : ""}`}
      replace
    />
  )
}
